/* =============================================================================
   VIEWS / ADMIN / SETTINGS-RESTAURANT — Restaurant-Sub-Tab (Stammdaten/Brand/Mail)
   =============================================================================
   S79 REF-6: aus settings.js extrahiert (größter Cut). Enthält:
     - renderRestaurantMainSubsection()   Stammdaten + Belegpflicht + Öffnung + Zeitzone
     - renderRestaurantBrandSubsection()   Design/Brand-Farben + Logo-Feld + Mail-Look
     - renderRestaurantMailSubsection()    Mail-Versand-Identität (senderName/senderEmail)
     - renderBrandLogoField() + _buildBrandLogoStatusLabel() + _isRelativeLogoUrlSafe()
     - Brand-Action-Handler: uploadBrandLogo / resetBrandLogo / updateBrandSenderField /
       updateRestaurantMeta / updateBrandField / applyBrandTheme

   Der dünne Dispatcher `renderRestaurantSection()` + die Sub-Sub-Tab-Bar bleiben
   im Haupt-`settings.js` (Cross-Cutting). Anti-Circular (vgl. settings-delivery-
   rules.js): keine Imports aus settings.js. Die zentrale Single-Source-Schreib-
   Funktion `updateSetting` + `renderSettings` werden über window-Wrapper
   aufgerufen (settings.js setzt beide via Object.assign auf window). Inline-
   Handler (updateSetting/updateRestaurantMeta/updateBrandField/…) resolven
   ohnehin zur Laufzeit über window.

   Re-Exports der Action-Handler + Test-Hook (_buildBrandLogoStatusLabel) +
   applyBrandTheme (von bootstrap.js statisch importiert) erfolgen in settings.js.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import {
  DEFAULT_LOGO_URL,
  applyBrand,
  getBrandLogoSrc,
  getBrandSnapshot
} from '../../core/branding.js';
import { extractLogoColors, ensureContrastOnDark, darkenHex } from '../../core/logo-colors.js';
import { _isValidTeamEmail } from './settings-team.js';
import { getSlotStepMin } from '../../core/reservation-engine.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

// Cross-Module-Wrapper für settings.js-internas (Anti-Circular). settings.js
// exportiert updateSetting (Single-Source-Write) + renderSettings via window.
function _updateSetting(key, val) {
  if (typeof window !== 'undefined' && typeof window.updateSetting === 'function') {
    return window.updateSetting(key, val);
  }
  return Promise.resolve();
}
function _renderSettings() {
  if (typeof window !== 'undefined' && typeof window.renderSettings === 'function') {
    window.renderSettings();
  }
}

// -----------------------------------------------------------------------------
// Section: Restaurant — Stammdaten, Adresse, Belegpflichtangaben, Öffnung, Design
// -----------------------------------------------------------------------------
export function renderRestaurantMainSubsection() {
  const state = getState();
  const s = state.settings;
  // S142 — effektives Zeitraster (Single-Source) für Anzeige == Verhalten.
  const slotStep = getSlotStepMin();
  return `
    <div class="settings-grid">
      <div class="card">
        <h2>Restaurant-Informationen</h2>
        <div class="field">
          <label>Name</label>
          <input type="text" value="${htmlEscape(s.restaurantName)}" oninput="updateSetting('restaurantName', this.value)">
        </div>
        <div class="field">
          <label>E-Mail-Adresse</label>
          <input type="email" value="${htmlEscape(s.restaurantEmail)}" oninput="updateSetting('restaurantEmail', this.value)">
        </div>
        <div class="field">
          <label>Telefon</label>
          <input type="tel" value="${htmlEscape(s.restaurantPhone)}" oninput="updateSetting('restaurantPhone', this.value)">
        </div>
        <div class="field">
          <label>Steuerberater-Mail (für Z-Bon-Versand)</label>
          <input type="email"
                 value="${htmlEscape(s.accountantEmail || '')}"
                 oninput="updateSetting('accountantEmail', this.value)"
                 placeholder="z.B. kanzlei@steuerberater.de — leer = Versand an Restaurant-Mail">
          <small class="hint" style="display:block; margin-top:4px;">
            Empfänger für die Aktion «Z-Bon versenden» im Tagesabschluss-Modal.
            Wenn leer, fällt der Versand auf die E-Mail-Adresse oben zurück.
          </small>
        </div>
        <h3 style="margin-top: 18px; margin-bottom: 8px;">Belegpflichtangaben</h3>
        <p class="hint" style="margin-bottom: 12px;">
          Diese Daten erscheinen auf jedem Kassenbon und Tagesabschluss.
          Pflichtangaben gemäß §&nbsp;14 UStG für Kleinbetragsrechnungen
          (USt-IdNr <em>oder</em> Steuernummer ist zwingend).
        </p>
        <div class="field">
          <label>Vollständiger Firmenname (inkl. Rechtsform)</label>
          <input type="text"
                 value="${htmlEscape((s.restaurant && s.restaurant.legalName) || '')}"
                 oninput="updateRestaurantMeta('legalName', this.value)"
                 placeholder="z.B. Grill & Green GmbH oder Max Mustermann">
        </div>
        <div class="field-row">
          <div class="field">
            <label>Straße + Hausnummer</label>
            <input type="text"
                   value="${htmlEscape((s.restaurant && s.restaurant.street) || '')}"
                   oninput="updateRestaurantMeta('street', this.value)"
                   placeholder="Musterstraße 12">
          </div>
          <div class="field">
            <label>PLZ</label>
            <input type="text"
                   value="${htmlEscape((s.restaurant && s.restaurant.zip) || '')}"
                   oninput="updateRestaurantMeta('zip', this.value)"
                   placeholder="12345"
                   maxlength="5"
                   style="max-width: 100px;">
          </div>
          <div class="field">
            <label>Ort</label>
            <input type="text"
                   value="${htmlEscape((s.restaurant && s.restaurant.city) || '')}"
                   oninput="updateRestaurantMeta('city', this.value)"
                   placeholder="Musterstadt">
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label>USt-IdNr</label>
            <input type="text"
                   value="${htmlEscape((s.restaurant && s.restaurant.ustId) || '')}"
                   oninput="updateRestaurantMeta('ustId', this.value)"
                   placeholder="DE123456789">
          </div>
          <div class="field">
            <label>Steuernummer (alternativ)</label>
            <input type="text"
                   value="${htmlEscape((s.restaurant && s.restaurant.taxNumber) || '')}"
                   oninput="updateRestaurantMeta('taxNumber', this.value)"
                   placeholder="123/4567/8901">
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label>Öffnung</label>
            <input type="time" step="${slotStep * 60}" value="${htmlEscape(s.openingTimeStart)}" onchange="updateSetting('openingTimeStart', this.value)">
            <div class="hint">In ${slotStep}-Min-Schritten wählbar.</div>
          </div>
          <div class="field">
            <label>Schließung</label>
            <input type="time" step="${slotStep * 60}" value="${htmlEscape(s.openingTimeEnd)}" onchange="updateSetting('openingTimeEnd', this.value)">
            <div class="hint">Letzter Reservierungs-Slot endet eine Reservierungsdauer vorher.</div>
          </div>
        </div>
        <div class="field">
          <label>Maximale Reservierungsdauer (Min.)</label>
          <input type="number" min="30" max="240" step="30" value="${s.slotDurationMin}" oninput="updateSettingClampedInt('slotDurationMin', this.value, 30, 240, 120)">
          <div class="hint">So lange wird der Tisch maximal pro Reservierung blockiert — danach wieder frei für die nächste Buchung.</div>
        </div>
        <div class="field">
          <label>Zeitraster (Minuten)</label>
          <select onchange="updateSettingClampedInt('slotStepMin', this.value, 5, 60, 15)">
            ${[10, 15, 30].map(v => {
              const sel = slotStep === v ? ' selected' : '';
              return `<option value="${v}"${sel}>${v} Minuten</option>`;
            }).join('')}
          </select>
          <div class="hint">Granularität der wählbaren Zeiten im Buchungsraster (Kundenformular) und der operativen Belegungs-Timeline. Ändert NICHT die Reservierungsdauer oder die Kapazitätszahlen — nur, wie fein die Slots getaktet sind.</div>
        </div>
        <div class="field">
          <label>Zeitzone</label>
          <select onchange="updateSetting('timezone', this.value)">
            ${[
              ['Europe/Berlin',    'Berlin (Deutschland, MEZ/MESZ)'],
              ['Europe/Vienna',    'Wien (Österreich, MEZ/MESZ)'],
              ['Europe/Zurich',    'Zürich (Schweiz, MEZ/MESZ)'],
              ['Europe/Amsterdam', 'Amsterdam (Niederlande, MEZ/MESZ)'],
              ['Europe/Paris',     'Paris (Frankreich, MEZ/MESZ)'],
              ['UTC',              'UTC (Universal Time, ohne Sommerzeit)']
            ].map(([tz, label]) => {
              const sel = (s.timezone || 'Europe/Berlin') === tz ? ' selected' : '';
              return `<option value="${htmlEscape(tz)}"${sel}>${htmlEscape(label)}</option>`;
            }).join('')}
          </select>
          <div class="hint">Bestimmt Tagesgrenze für Z-Bon, DSFinV-K-Export und Reminder. Default: Europe/Berlin (deutsches Steuerrecht).</div>
        </div>
      </div>
    </div>
  `;
}

export function renderRestaurantBrandSubsection() {
  const state = getState();
  const s = state.settings;
  return `
    <div class="settings-grid">
      <div class="card">
        <h2>Design / Brand</h2>
        ${renderBrandLogoField()}
        <div class="field-row">
          <div class="field">
            <label>Anzeige-Name</label>
            <input type="text"
                   value="${htmlEscape((s.brand && s.brand.displayName) || '')}"
                   oninput="updateBrandField('displayName', this.value)"
                   placeholder="z.B. Grill &amp; Green">
            <div class="hint">Erscheint im Cockpit-Header, im Mail-Wrap und auf dem Bon. Leer = Restaurant-Name als Fallback.</div>
          </div>
          <div class="field">
            <label>Tagline / Untertitel</label>
            <input type="text"
                   value="${htmlEscape((s.brand && s.brand.tagline) || '')}"
                   oninput="updateBrandField('tagline', this.value)"
                   placeholder="z.B. Burger · Juice · Bowls">
            <div class="hint">Kleine Zeile unter dem Anzeige-Namen im Mail-Header. Leer lassen für „keine".</div>
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label>Hauptfarbe</label>
            <input type="color" value="${(s.brand && s.brand.primaryColor) || s.brandPrimaryColor || '#2f6b3a'}" oninput="updateBrandField('primaryColor', this.value)">
            <div class="hint">Wird für Header, Buttons, Mail-Marken-Schriftfarbe und Links verwendet.</div>
          </div>
          <div class="field">
            <label>Akzentfarbe</label>
            <input type="color" value="${(s.brand && s.brand.accentColor) || s.brandAccentColor || '#d97a2c'}" oninput="updateBrandField('accentColor', this.value)">
            <div class="hint">Für Highlights, Manuell-Badges und CTA-Akzente.</div>
          </div>
          <div class="field">
            <label>Logo-Text-Fallback</label>
            <input type="text"
                   value="${htmlEscape((s.brand && s.brand.logoText) || '')}"
                   oninput="updateBrandField('logoText', this.value)"
                   placeholder="z.B. G&amp;G"
                   maxlength="6">
            <div class="hint">Kurz-Kürzel, wenn kein Logo-Bild hinterlegt ist. 1-6 Zeichen.</div>
          </div>
        </div>
        <div style="margin-top:6px;">
          <button type="button" class="btn btn-ghost btn-sm" onclick="applyLogoColors()" style="margin:0;">
            🎨 Farben aus Logo übernehmen
          </button>
          <div class="hint">Leitet Haupt- + Akzentfarbe aus dem hinterlegten Logo ab (überschreibt die beiden Felder oben). Beim Logo-Upload werden leere/Standard-Felder automatisch vorbelegt.</div>
        </div>
        <hr style="margin:20px 0;border:none;border-top:1px solid var(--border);">
        <h3 style="margin:0 0 12px 0;font-size:14px;color:var(--ink-soft,#5a6660);">Mail-Look (dunkles Brand-Design)</h3>
        <div class="field-row">
          <div class="field">
            <label>Mail-Hintergrund</label>
            <input type="color" value="${(s.brand && s.brand.backgroundColor) || '#1a1d1f'}" oninput="updateBrandField('backgroundColor', this.value)">
            <div class="hint">Äußerer Hintergrund der E-Mails. Default: Anthrazit #1a1d1f.</div>
          </div>
          <div class="field">
            <label>Card-Hintergrund</label>
            <input type="color" value="${(s.brand && s.brand.cardBackgroundColor) || '#22262a'}" oninput="updateBrandField('cardBackgroundColor', this.value)">
            <div class="hint">Innere Mail-Karte. Default: leicht heller als Mail-BG.</div>
          </div>
          <div class="field">
            <label>Mail-Textfarbe</label>
            <input type="color" value="${(s.brand && s.brand.bodyTextColor) || '#e8e6df'}" oninput="updateBrandField('bodyTextColor', this.value)">
            <div class="hint">Default: cremefarben #e8e6df — gute Lesbarkeit auf dunkel.</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function renderRestaurantMailSubsection() {
  const state = getState();
  const s = state.settings;
  return `
    <div class="settings-grid">
      <div class="card">
        <h2>Mail-Versand-Identität</h2>
        <p class="hint" style="margin-bottom: 12px;">
          Absender für Bestätigungs-, Reminder- und Storno-Mails. Wenn leer,
          nutzen wir den Provider-Default (<code>noreply@yourgrowth365.de</code>
          via YOUR GROWTH 365). Eigene Domain möglich, sobald du sie bei Resend
          verifiziert hast — sonst lehnt Resend den Versand ab.
        </p>
        <div class="field-row">
          <div class="field">
            <label>Absender-Name</label>
            <input type="text"
                   value="${htmlEscape((s.brand && s.brand.senderName) || '')}"
                   oninput="updateBrandSenderField('senderName', this.value)"
                   placeholder="z.B. Grill &amp; Green Reservierung">
            <div class="hint">Erscheint im Postfach des Empfängers als Absender-Anzeigename.</div>
          </div>
          <div class="field">
            <label>Absender-Adresse</label>
            <input type="email"
                   value="${htmlEscape((s.brand && s.brand.senderEmail) || '')}"
                   oninput="updateBrandSenderField('senderEmail', this.value)"
                   placeholder="z.B. reservierung@grill-and-green.de">
            <div class="hint">
              Muss bei Resend verifiziert sein (DKIM + SPF + MX). Ungültige oder unverifizierte Adressen führen zu Mail-Bounces.
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Brand-Logo (Phase 31, Session 29)
// -----------------------------------------------------------------------------
// Logo-Upload-Feld + Preview + Reset für settings.brand.{logoUrl, logoUploadedAt}.
// Storage-Bucket `restaurant-logos` (Migration 68), public read + authenticated
// write. Default-Fallback `assets/logo.png` aus dem Repo (Brand-Helper
// src/core/branding.js).
// -----------------------------------------------------------------------------

const BRAND_LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB — Match zur Bucket-Policy
const BRAND_LOGO_MIME_ALLOW = ['image/png', 'image/svg+xml', 'image/jpeg'];

/** Pure: Welcher Status-Text steht oben im Logo-Feld?
 *  Tenant-aware ab S49 Block C (Memory project_gg_firma_rollen):
 *  - Pilot (rest_default) + isDefault → „Standard-Logo (assets/logo.png)"
 *  - andere Tenants + isDefault       → „Kein Logo gesetzt — Text-only Header"
 *  - jeder Tenant + eigenes Logo      → „Eigenes Logo · hochgeladen …"
 *  Wird in Tests verifiziert (settings.test.js, S49 Δ +Cases).
 */
export function _buildBrandLogoStatusLabel(snap, restaurantId) {
  if (!snap || snap.isDefault) {
    if (restaurantId === 'rest_default') {
      return 'Standard-Logo (assets/logo.png)';
    }
    return 'Kein Logo gesetzt — Text-only Header';
  }
  if (snap.logoUploadedAt) {
    const stamp = String(snap.logoUploadedAt).replace('T', ' ').slice(0, 16);
    return `Eigenes Logo · hochgeladen ${stamp}`;
  }
  return 'Eigenes Logo';
}

/** Rendert das Logo-Feld (Preview + Upload-Button + Reset) für die
 *  Restaurant-Section. Wird aus renderRestaurantBrandSubsection() aufgerufen. */
export function renderBrandLogoField() {
  const snap = getBrandSnapshot();
  const src = htmlEscape(getBrandLogoSrc());
  const restId = (window.App && typeof window.App.getCurrentRestaurantId === 'function')
    ? window.App.getCurrentRestaurantId()
    : 'rest_default';
  const statusLabel = _buildBrandLogoStatusLabel(snap, restId);
  // S49 Block C: bei nicht-Pilot-Tenants OHNE eigenes Logo zeigt das Preview-
  // Bild den Repo-Default. Optisch nicht hilfreich für Demo-Tester. Wir setzen
  // das Bild dann auf einen neutralen Placeholder mit Opacity, damit klar ist
  // „hier ist nichts hochgeladen".
  const showPlaceholder = snap && snap.isDefault && restId !== 'rest_default';
  const imgOpacity = showPlaceholder ? '0.35' : '1';
  // S58 Block I.5: Warnung anzeigen, wenn settings.brand.logoUrl als
  // relative URL gespeichert ist — externe Mail-Clients können sie nicht
  // auflösen. Hard-Way-Fix ist Re-Upload via Storage-Bucket.
  const isRelative = !!(snap && !snap.isDefault && _isRelativeLogoUrlSafe(snap.logoUrl));
  const relativeWarning = isRelative
    ? `<div class="hint" style="margin-top:6px; color:var(--accent); font-weight:600;">
         ⚠️ Logo-URL ist relativ ("${htmlEscape(snap.logoUrl)}"). Externe
         Mail-Clients zeigen dann ein leeres Bild. Bitte über „Logo hochladen"
         neu hochladen — der Storage-Bucket liefert dann eine absolute URL.
       </div>`
    : '';
  return `
    <div class="field" style="margin-bottom: 18px;">
      <label>Logo</label>
      <div style="display:flex; gap:16px; align-items:center; padding:12px; background:var(--bg-soft, #f6f6f6); border-radius:8px; border:1px solid var(--border, #e2e2e2);">
        <img id="brand-logo-preview"
             src="${src}"
             alt="Restaurant-Logo"
             style="width:96px; height:96px; object-fit:contain; background:var(--bg-elev); border-radius:6px; border:1px solid var(--border, #e2e2e2); opacity:${imgOpacity};"
             onerror="this.onerror=null; this.src='${htmlEscape(DEFAULT_LOGO_URL)}';">
        <div style="flex:1; min-width:0;">
          <div style="font-size:13px; color:var(--ink-muted); margin-bottom:8px;">${htmlEscape(statusLabel)}</div>
          <div style="display:flex; gap:8px; flex-wrap:wrap;">
            <label class="btn" style="cursor:pointer; margin:0;">
              <input type="file"
                     id="brand-logo-input"
                     accept="image/png,image/svg+xml,image/jpeg"
                     style="display:none;"
                     onchange="uploadBrandLogo(this)">
              📤 Logo hochladen
            </label>
            ${snap.isDefault ? '' : `
              <button type="button" class="btn btn-ghost" onclick="resetBrandLogo()" style="margin:0;">
                ↺ Auf Standard zurücksetzen
              </button>`}
          </div>
          <div class="hint" style="margin-top:8px;">
            Max. 2&nbsp;MB. PNG, JPEG oder SVG. Erscheint auf Customer-Formular,
            Cockpit-Header, Bon-Beleg, Z-Bon, Mail-Templates und im Browser-Tab.
          </div>
          ${relativeWarning}
        </div>
      </div>
    </div>
  `;
}

/** S58 Block I.5 — defensive Prüfung, ob eine gespeicherte Logo-URL relativ
 *  ist. Wird nur für die Settings-UI-Warnung genutzt. Branding-Modul exportiert
 *  `isRelativeLogoUrl` als kanonische Variante; diese hier ist ein Inline-
 *  Fallback, damit ein Import-Order-Edge-Case (settings.js wird vor branding.js
 *  geladen) keine Crashes verursacht. */
function _isRelativeLogoUrlSafe(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const t = url.trim();
  if (/^https?:\/\//i.test(t)) return false;
  if (/^data:/i.test(t))       return false;
  // assets/* und /assets/* sind die häufigsten Tenant-Fehlfälle.
  return true;
}

/** File-Input-Handler — validiert die Datei, lädt in den Storage-Bucket
 *  `restaurant-logos`, schreibt `settings.brand.{logoUrl, logoUploadedAt}`. */
export async function uploadBrandLogo(input) {
  const file = input && input.files && input.files[0];
  if (!file) return;

  // Validation
  if (!BRAND_LOGO_MIME_ALLOW.includes(file.type)) {
    showToast(`Dateiformat nicht unterstützt (${file.type || 'unbekannt'}). Erlaubt: PNG, JPEG, SVG.`, 'error');
    input.value = '';
    return;
  }
  if (file.size > BRAND_LOGO_MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    showToast(`Datei zu groß (${mb} MB). Maximum: 2 MB.`, 'error');
    input.value = '';
    return;
  }

  const supa = window.App && window.App.supabase;
  if (!supa || !supa.storage) {
    showToast('Storage-Client nicht verfügbar. Bitte Seite neu laden.', 'error');
    return;
  }

  // Restaurant-ID + eindeutiger Dateiname (verhindert Browser-Cache-Hits
  // bei wiederholtem Upload + erlaubt mehrere Restaurants im selben Bucket)
  const restId = (window.App && window.App.getCurrentRestaurantId)
    ? window.App.getCurrentRestaurantId()
    : 'rest_default';
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  const ts = Date.now();
  const objectPath = `${restId}/logo-${ts}.${ext}`;

  showToast('Lade Logo hoch…', 'info');
  try {
    const { error: upErr } = await supa.storage
      .from('restaurant-logos')
      .upload(objectPath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });
    if (upErr) throw upErr;

    // Public-URL holen (Bucket ist public, dauerhaft gültig)
    const { data: pubData } = supa.storage
      .from('restaurant-logos')
      .getPublicUrl(objectPath);
    const publicUrl = pubData && pubData.publicUrl;
    if (!publicUrl) throw new Error('Public-URL konnte nicht erzeugt werden');

    // settings.brand atomar schreiben (komplettes Sub-Objekt wegen JSONB-Merge)
    const state = getState();
    const curBrand = (state.settings && state.settings.brand) || {};
    const nextBrand = {
      ...curBrand,
      logoUrl: publicUrl,
      logoUploadedAt: new Date().toISOString()
    };
    await _updateSetting('brand', nextBrand);

    applyBrand();
    // S167 P4 — Brand-Farben aus dem frisch hochgeladenen Logo VORBELEGEN.
    // Best-effort, liest die File-Blob client-seitig (kein Re-Fetch). Kein
    // Clobber: nur leere/Default-Farbfelder werden gesetzt (onlyEmpty). Schlägt
    // die Extraktion fehl (z.B. cross-origin / kaputtes Bild), bleibt alles wie
    // gehabt. Toast „Logo aktualisiert" kommt danach, ggf. ergänzt um die Farben.
    let colorNote = '';
    try {
      const filled = await _applyLogoColors(file, { onlyEmpty: true });
      if (filled && filled.length) colorNote = ` Farben vorbelegt: ${filled.join(' + ')}.`;
    } catch (_) { /* defensive — Branding ist UX */ }
    showToast('Logo aktualisiert.' + colorNote, 'success');
    _renderSettings();
  } catch (e) {
    console.error('[uploadBrandLogo] Upload fehlgeschlagen:', e);
    showToast('Logo konnte nicht hochgeladen werden: ' + (e.message || e), 'error');
  } finally {
    input.value = '';
  }
}

/** Setzt das Logo auf den Repo-Default zurück (logoUrl=null).
 *  Storage-Objekt bleibt im Bucket — wir löschen es nicht aktiv (für ggf.
 *  spätere Restore-Möglichkeit). */
export async function resetBrandLogo() {
  if (!(await window.styledConfirm({ title: 'Logo auf Standard zurücksetzen?', danger: true, confirmLabel: 'Zurücksetzen' })).confirmed) return;
  const state = getState();
  const curBrand = (state.settings && state.settings.brand) || {};
  const nextBrand = {
    ...curBrand,
    logoUrl: null,
    logoUploadedAt: null
  };
  try {
    await _updateSetting('brand', nextBrand);
    applyBrand();
    showToast('Logo zurückgesetzt.', 'success');
    _renderSettings();
  } catch (e) {
    console.error('[resetBrandLogo]', e);
    showToast('Reset fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S49 Block D — Mail-Sender-Identität pro Tenant.
 * Schreibt `settings.brand.{senderEmail, senderName}`. Edge-Functions
 * send-mail + process-mail-outbox lesen diese Werte server-side via
 * Service-Role und überschreiben den RESEND_FROM-ENV-Default.
 *
 * Validation:
 *  - senderEmail: leer = Fallback, sonst muss Email-Format passen.
 *    UI zeigt einen Toast bei Ungültigkeit, persistiert aber den Wert
 *    trotzdem (User soll seinen Tippfehler korrigieren können, nicht
 *    den Input verlieren). Edge-Function macht eine zweite, harte
 *    Validation und lehnt ungültige Werte ab.
 *  - senderName: keine Format-Validation, Whitespace wird getrimmt.
 */
export async function updateBrandSenderField(field, value) {
  if (field !== 'senderEmail' && field !== 'senderName') return;
  const state = getState();
  const curBrand = (state.settings && state.settings.brand) || {};
  const cleaned = (typeof value === 'string') ? value.trim() : '';
  const nextBrand = { ...curBrand, [field]: cleaned };
  await _updateSetting('brand', nextBrand);
  // Live-Hinweis bei Ungültigkeit (nicht-blockierend, persistiert trotzdem)
  if (field === 'senderEmail' && cleaned && !_isValidTeamEmail(cleaned)) {
    showToast('Hinweis: Diese Adresse ist kein gültiges E-Mail-Format und wird vom Server abgelehnt.', 'info');
  }
}

/**
 * Unternehmensdaten / Belegpflichtangaben aktualisieren (Session 13.1).
 * Speichert das komplette `restaurant`-Sub-Objekt zurück, damit der JSONB-
 * Merge konsistent bleibt. Wird vom Bon-Renderer ausgelesen (legalName,
 * street, zip, city, ustId, taxNumber).
 */
export async function updateRestaurantMeta(field, value) {
  const state = getState();
  const cur   = (state.settings && state.settings.restaurant) || {};
  const next  = { ...cur, [field]: value };
  await _updateSetting('restaurant', next);
}

/** Brand-Farben aus Settings als CSS-Custom-Properties anwenden.
 *  S56-Hotfix-5: liest primär aus `settings.brand.{primaryColor, accentColor}`
 *  (neue Konvention, konsistent mit Mail-Renderer + restaurants.settings.brand
 *  in der DB). Fallback auf alte flache Keys `brandPrimaryColor` /
 *  `brandAccentColor` für Tenants, die noch nicht migriert wurden. */
export function applyBrandTheme() {
  const state = getState();
  if (!state || !state.settings) return;
  const r = document.documentElement;
  const brand = (state.settings.brand && typeof state.settings.brand === 'object') ? state.settings.brand : {};
  const primary = (typeof brand.primaryColor === 'string' && brand.primaryColor.trim())
    || state.settings.brandPrimaryColor;
  const accent  = (typeof brand.accentColor === 'string' && brand.accentColor.trim())
    || state.settings.brandAccentColor;
  if (primary) {
    r.style.setProperty('--green', primary);
    // S167 P4 — `-dark`-Variante mit ableiten, damit auch Guest-Popup-Titel +
    // aktive Service-Chips (nutzen var(--green-dark)) der Logo-Farbe folgen.
    try { r.style.setProperty('--green-dark', darkenHex(primary, 0.22)); } catch (_) { /* defensive */ }
  }
  if (accent) {
    r.style.setProperty('--accent', accent);
    try { r.style.setProperty('--accent-dark', darkenHex(accent, 0.18)); } catch (_) { /* defensive */ }
  }
}

/**
 * S56-Hotfix-5: Generischer Setter für settings.brand.{primaryColor, accentColor,
 * displayName, tagline, logoText}. Pendant zu updateBrandSenderField — gleiche
 * jsonb-Merge-Logik (kompletes brand-Objekt zurückschreiben), triggert
 * applyBrand danach, damit CSS-Vars + Mail-Renderer sofort den neuen Wert
 * lesen. Nur für Owner/Admin via Phase-41-Permission (Settings → Restaurant
 * ist owner+admin Write).
 */
export async function updateBrandField(field, value) {
  const allowed = new Set([
    'primaryColor', 'accentColor', 'displayName', 'tagline', 'logoText',
    // S56 Hotfix 7: dunkler Mail-Look
    'backgroundColor', 'cardBackgroundColor', 'bodyTextColor'
  ]);
  if (!allowed.has(field)) return;
  // S56 Hotfix 5: dynamischer Import von requireEditPermission, weil settings.js
  // historisch keinen statischen Import für den Helper hat. Pattern analog zu
  // anderen Lazy-Imports in dieser Datei (siehe z.B. orders.js wrapHtmlWithBrandHtml).
  try {
    const { requireEditPermission } = await import('../../utils/helpers.js');
    if (typeof requireEditPermission === 'function'
        && !requireEditPermission('settings.restaurant', `Brand-${field} ändern`)) {
      return;
    }
  } catch (_) { /* defensive — wenn Helper nicht ladbar, weiter (DB-RLS greift eh) */ }
  const state = getState();
  const curBrand = (state.settings && state.settings.brand && typeof state.settings.brand === 'object')
    ? state.settings.brand : {};
  const cleaned = (typeof value === 'string') ? value.trim() : '';
  const nextBrand = { ...curBrand, [field]: cleaned };
  await _updateSetting('brand', nextBrand);
  // CSS-Vars sofort live setzen (für primaryColor/accentColor)
  if (field === 'primaryColor' || field === 'accentColor') {
    try { applyBrandTheme(); } catch (_) { /* defensive */ }
  }
}

// -----------------------------------------------------------------------------
// S167 P4 — Brand-Farb-Auto-Extraktion aus dem Logo
// -----------------------------------------------------------------------------
// Pilot-Brand-Defaults: ein Tenant, der diese (oder leere) Werte trägt, hat die
// Farben nicht bewusst gesetzt → autofill darf sie ersetzen (kein Clobber).
const _BRAND_DEFAULT_PRIMARY = '#2f6b3a';
const _BRAND_DEFAULT_ACCENT  = '#d97a2c';
// Dunkler Mail-Hintergrund (siehe _resolveMailBrand-Default in branding.js) —
// Primär/Akzent müssen darauf als Text/Link lesbar sein (Kontrast-Sicherung).
const _MAIL_DARK_BG = '#1a1d1f';

/** Ist ein Farbfeld „leer oder Default" → darf vom Autofill gesetzt werden. */
function _isEmptyOrDefaultColor(val, def) {
  if (typeof val !== 'string' || !val.trim()) return true;
  return val.trim().toLowerCase() === def.toLowerCase();
}

/**
 * Leitet Haupt-/Akzentfarbe aus `source` (File-Blob ODER Logo-URL) ab und
 * schreibt sie nach `settings.brand`. Kontrast gegen den dunklen Mail-BG wird
 * gesichert (zu dunkle Töne aufgehellt).
 *
 *  - onlyEmpty=true  (Upload-Autofill): nur leere/Default-Felder setzen.
 *  - onlyEmpty=false (Button):          beide Felder überschreiben.
 *
 * @returns {Promise<string[]>} Liste der geänderten Feld-Labels (für Toast),
 *                              leer wenn nichts geändert wurde / Extraktion leer.
 */
async function _applyLogoColors(source, opts) {
  const onlyEmpty = !!(opts && opts.onlyEmpty);
  let res = null;
  try { res = await extractLogoColors(source); } catch (_) { res = null; }
  if (!res || (!res.primary && !res.accent)) return [];

  const state = getState();
  const curBrand = (state.settings && state.settings.brand && typeof state.settings.brand === 'object')
    ? state.settings.brand : {};
  const next = { ...curBrand };
  const changed = [];

  if (res.primary && (!onlyEmpty || _isEmptyOrDefaultColor(curBrand.primaryColor, _BRAND_DEFAULT_PRIMARY))) {
    next.primaryColor = ensureContrastOnDark(res.primary, _MAIL_DARK_BG);
    changed.push('Hauptfarbe');
  }
  if (res.accent && (!onlyEmpty || _isEmptyOrDefaultColor(curBrand.accentColor, _BRAND_DEFAULT_ACCENT))) {
    next.accentColor = ensureContrastOnDark(res.accent, _MAIL_DARK_BG);
    changed.push('Akzentfarbe');
  }
  if (!changed.length) return [];

  await _updateSetting('brand', next);
  try { applyBrandTheme(); } catch (_) { /* defensive */ }
  try { applyBrand(); } catch (_) { /* defensive */ }
  return changed;
}

/**
 * Inline-Handler „🎨 Farben aus Logo übernehmen": leitet die Brand-Farben aus
 * dem aktuell hinterlegten Logo ab und ÜBERSCHREIBT Haupt- + Akzentfeld
 * (expliziter User-Wunsch). Permission analog updateBrandField.
 */
export async function applyLogoColors() {
  try {
    const { requireEditPermission } = await import('../../utils/helpers.js');
    if (typeof requireEditPermission === 'function'
        && !requireEditPermission('settings.restaurant', 'Brand-Farben aus Logo ableiten')) {
      return;
    }
  } catch (_) { /* defensive — DB-RLS greift ohnehin */ }

  const src = getBrandLogoSrc();
  if (!src) { showToast('Kein Logo hinterlegt.', 'info'); return; }
  showToast('Lese Logo-Farben…', 'info');
  const changed = await _applyLogoColors(src, { onlyEmpty: false });
  if (changed.length) {
    showToast(`Brand-Farben aus Logo übernommen (${changed.join(' + ')}).`, 'success');
    _renderSettings();
  } else {
    showToast('Aus dem Logo ließ sich keine Farbe ableiten.', 'info');
  }
}
